'use client';

import {
  default as AdvisorIllustration,
  default as AnalystIllustration,
  default as AnimatorIllustration,
  default as CoachIllustration,
  default as DefaultIllustration,
  default as PeacemakerIllustration,
  default as PilotIllustration,
  default as PlannerIllustration,
  default as ProtectorIllustration,
} from '@/assets/backgrounds/evolution.svg';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Stack, Typography } from '@mui/material';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { Application, ButtonLink, translate } from '@prismo-io/router';
import {
  AuraProfileCode,
  type AuraProfileCodeT,
  LanguageCode,
  type LanguageCodeT,
} from '@prismo-io/schemas';
import Image from 'next/image';
import { type FC, useMemo } from 'react';

type Props = {
  order: number;
  profileType?: AuraProfileCodeT;
  userUrl: string;
  hasHistoric: boolean;
};

const buildKnowMoreUrl = (locale: LanguageCodeT, userUrl: string) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.APP,
    path: `/profil/:user/historique`,
    params: { user: userUrl },
    locale,
  });
};

export const HistoricCard: FC<Props> = (props: Props) => {
  const { profileType, order, userUrl, hasHistoric } = props;

  const me = useMe();

  const isMe = me && me.url === userUrl;

  const img = useMemo(() => {
    switch (profileType) {
      case AuraProfileCode.Enum.ADVISOR:
        return AdvisorIllustration;
      case AuraProfileCode.Enum.ANALYST:
        return AnalystIllustration;
      case AuraProfileCode.Enum.ANIMATOR:
        return AnimatorIllustration;
      case AuraProfileCode.Enum.COACH:
        return CoachIllustration;
      case AuraProfileCode.Enum.PEACEMAKER:
        return PeacemakerIllustration;
      case AuraProfileCode.Enum.PILOT:
        return PilotIllustration;
      case AuraProfileCode.Enum.PLANNER:
        return PlannerIllustration;
      case AuraProfileCode.Enum.PROTECTOR:
        return ProtectorIllustration;
      default:
        return DefaultIllustration;
    }
  }, [profileType]);

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;

  return (
    <Stack
      component="article"
      bgcolor={hasHistoric ? 'background.primary' : 'white'}
      borderRadius="3rem"
      p={2}
      minWidth={{ none: '100%', sm: '45%', lg: '31%' }}
      sx={{
        boxShadow: '2px 2px 7px 0px rgba(0,0,0,0.3)',
        flex: '1 1 0%',
        order: order,
      }}
      direction="column"
      alignItems="center"
      spacing={2}
    >
      <Box component="div" position="relative" width="10.5rem" height="10.5rem">
        <Image
          src={img}
          alt={t`Profil Adapté Aura | PRISMO`}
          style={{ objectFit: 'contain', objectPosition: 'center' }}
          fill
        />
      </Box>
      <Box component="div">
        <ButtonLink
          app={Application.APP}
          path="/profil/:user/historique"
          params={{ user: userUrl }}
          prefetch={false}
          disabled={!hasHistoric}
          variant="contained"
          color="gradient"
        >
          <Trans>Découvrez</Trans>
        </ButtonLink>
      </Box>
      {hasHistoric && (
        <Stack direction="row" gap="3px">
          <Typography variant="h3" textAlign="center">
            {isMe && <Trans>Découvrez l'évolution de votre profil</Trans>}
            {!isMe && (
              <Trans>Découvrez l'évolution du profil de cet utilisateur</Trans>
            )}
          </Typography>
        </Stack>
      )}
      {!hasHistoric && (
        <Stack direction="row" gap="3px">
          <Typography variant="h3" textAlign="center">
            {isMe && (
              <Trans>
                L'évolution de votre profil n'est pas encore disponible
              </Trans>
            )}
            {!isMe && (
              <Trans>
                L'évolution du profil de cet utilisateur n'est pas encore
                disponible
              </Trans>
            )}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
